import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * Comparator kinds are a finite CODE-level set — the MECHANISM half of the
 * matching engine. A tenant chooses WHICH comparator an attribute uses; it can
 * never invent a new one, because a kind is an algorithm, not a value.
 *
 * An attribute with no special kind gets `exact` (exact match after
 * normalization), so a novel attribute from a tenant's vocabulary is comparable
 * on day one without a code change.
 */
export type MatchingAttributeComparatorKind =
	| 'length'
	/**
	 * A number in the attribute's OWN unit, with an absolute tolerance. The
	 * domain-neutral measurement comparator: `length` is millimetre-specific by
	 * construction, and a voltage, a mass, a volume or a pack size has no
	 * business being described in millimetres to be compared.
	 */
	| 'numeric'
	/**
	 * A DESIGNATION MADE OF PARTS — `M12x1.75`, `A2-70`, `1/2-13 UNC`, `E27x60`.
	 * The comparator is generic: the PATTERNS that say which parts a designation
	 * has are tenant data (`patterns` below), so the engine reads a fastener
	 * thread and a lamp-base code with the same code and knows neither domain.
	 */
	| 'structuredCode'
	| 'norm'
	| 'synonym'
	/**
	 * A code whose JOB is to identify the exact article — a part number, an MPN,
	 * a GTIN, a catalog number.
	 *
	 * An identifier is not weighed against the other attributes; it feeds the
	 * IDENTIFIER pass, where a hit is the strongest evidence the engine has. The
	 * kind exists so that "this column is a part number" is stated where every
	 * other fact about a column is stated — on the attribute itself — rather
	 * than in a second, free-form list of field keys that could disagree with it.
	 */
	| 'identifier'
	| 'exact';

/** One attribute the engine compares, as the tenant's schema declares it. */
export interface MatchingSchemaAttributeModel {
	/** Display label used in the divergence prose a reviewer reads. */
	label: string;
	/** Which code comparator judges this attribute. */
	kind: MatchingAttributeComparatorKind;
	/**
	 * Relative comparison mass. Bounded on write — see the DTO's constants.
	 *
	 * IGNORED for `kind: 'identifier'`, which is not part of the weighted
	 * comparison at all: an identifier is judged by the identifier pass, and
	 * counting a part number a second time as an agreeing attribute would
	 * inflate the confidence of a match it already decided.
	 */
	weight: number;
	/** A contradiction here pins the row to the review band. */
	critical: boolean;
	/** Only meaningful for `kind: 'length'`; absent means exact agreement. */
	toleranceMm?: number;
	/**
	 * Only meaningful for `kind: 'numeric'`; absent means exact agreement.
	 * ABSOLUTE, in this attribute's own unit — kept separate from `toleranceMm`
	 * rather than reusing it, because a stored fastener document means
	 * MILLIMETRES by that name and renaming it would silently retune every
	 * existing length attribute.
	 */
	tolerance?: number;
	/**
	 * Only meaningful for `kind: 'numeric'`: the unit the numbers are read in,
	 * for display. DECLARATIVE — the comparator never converts, because a
	 * conversion table is domain knowledge the engine does not have.
	 */
	unit?: string;
	/**
	 * Only meaningful for `kind: 'structuredCode'`: the ordered regular-expression
	 * alternatives that read a designation's PARTS, each part a named capture
	 * group. The first alternative that matches a side decides how that side is
	 * read, so the more specific alternative comes first.
	 *
	 * This is the whole reason the comparator is domain-free: `M12x1.75` and
	 * `A2-70` are read by patterns a tenant writes, never by code that knows what
	 * a thread is.
	 */
	patterns?: Array<string>;
}

/**
 * A synonym table for one attribute: what this catalog calls a value.
 *
 * `canonical` maps a canonical value onto the aliases that fold onto it,
 * `patterns` carries regex aliases (`T25`, `SW17`), and `subsumes` names a
 * broader value that must not contradict its narrower children.
 */
export interface MatchingSchemaSynonymGroupModel {
	canonical: Record<string, Array<string>>;
	patterns?: Record<string, Array<string>>;
	subsumes?: Record<string, Array<string>>;
}

/**
 * A per-category attribute policy override.
 *
 * Most catalogs have a long tail of one-off categories; a category with no
 * entry scores on the schema's own attribute list, which is why every field
 * here is optional.
 */
export interface MatchingSchemaCategoryOverrideModel {
	attributes?: Record<string, MatchingSchemaAttributeModel>;
	identityAttributes?: Array<string>;
}

/**
 * The tenant's matching SCHEMA: the domain vocabulary the engine reasons in.
 *
 * This is the DATA half of the split the engine rests on. Everything here is a
 * fact about a product domain — which attributes exist, what they are called,
 * which ones constitute an article's identity, what this catalog's words mean —
 * and none of it can be known in code, because the code has to serve fasteners,
 * pharmaceuticals and hydraulic fittings from the same engine.
 *
 * Everything that decides what a CONFIDENCE NUMBER MEANS — the bands, the caps,
 * the ceilings, the parsed-evidence discount, the feedback points, the
 * auto-assign shape test, the identifier column policy — stays in code and is
 * injected by the compiler. A document that mentions any of it is rejected
 * rather than ignored, so nobody can read this screen and believe a cap moved.
 *
 * A tenant with NO stored document has no schema at all. The engine refuses to
 * score for it rather than borrowing another catalog's vocabulary, because a
 * confident number about a catalog nobody has described is worse than a
 * refusal: the refusal is visible and the wrong number is not.
 */
export interface MatchingSchemaDocumentModel {
	/** attribute key → its declaration. The LIST itself is tenant data. */
	attributes: Record<string, MatchingSchemaAttributeModel>;
	/**
	 * Attributes that CONSTITUTE the article's identity. A high confidence band
	 * additionally requires at least one of these to have been compared AND
	 * agreed. May be empty — the identity rail then simply never fires.
	 */
	identityAttributes: Array<string>;
	/** Per-category attribute policy, merged over the top-level attributes. */
	categories: Record<string, MatchingSchemaCategoryOverrideModel>;
	/** attribute key → its synonym table. Only `synonym`-kind attributes use one. */
	synonyms: Record<string, MatchingSchemaSynonymGroupModel>;
	/**
	 * Groups of standard NUMBERS that name the same article (DIN 912 was
	 * superseded by ISO 4762). Grouped by number because the issuing bodies
	 * differ by design.
	 */
	normEquivalences: Array<Array<string>>;
	/**
	 * Token folding for the wording-similarity pass only, so a catalog line and
	 * a customer line in different languages land on the same tokens. Not
	 * authoritative for attribute verdicts. An empty mapping deletes the token.
	 */
	lexicalSynonyms: Record<string, string>;
	/**
	 * Words that carry no product meaning, excluded from the wording pass.
	 *
	 * NOTE on identifiers: which of this tenant's columns hold an article's own
	 * identity is stated by declaring an attribute with `kind: 'identifier'` and
	 * linking the catalog column to it through the attribute-alias dictionary.
	 * There is no separate list of identifier field keys — a part-number column
	 * is an attribute whose job is identification, and two places to say so
	 * could only ever agree or be wrong about each other.
	 */
	stopTokens: Array<string>;
}

/**
 * "This tenant's matching engine reasons in THIS domain vocabulary."
 *
 * WHY the row exists: the engine's mechanism is code, but its vocabulary cannot
 * be. A hardcoded attribute list is a hardcoded product domain, and the same
 * engine has to serve a fastener distributor and a hydraulics distributor
 * without either of them waiting on a release.
 *
 * The ABSENCE of a row is a tenant that has configured NOTHING: the engine
 * ships comparison methods and no vocabulary, so it refuses to score until a
 * row exists. A row is written on the Matching rules screen, from a proposal
 * generated off the tenant's own catalog, or by importing a document another
 * tenant exported.
 *
 * `note` is not decoration. Changing a weight or retiring an attribute silently
 * re-ranks every future match, so the next person to open this screen has to be
 * able to read why the schema says what it says.
 */
@Index('uq_crmp_profile', ['profileKey'], { unique: true })
@Entity('cross_reference_matching_profile')
export class CrossReferenceMatchingProfileEntity extends BaseEntity {
	/**
	 * The profile this document applies to. Today the only key is the sentinel
	 * `DEFAULT_MATCHING_PROFILE_KEY`: per-category policy lives INSIDE the
	 * document, so a second row would hold the same facts in two places. The
	 * column stays so a future second profile scope is additive.
	 */
	@Column('varchar', { name: 'profileKey', length: 64 })
	public profileKey!: string;

	/** The FULL schema document. Validated on write, never merged on read. */
	@Column('json', { name: 'document' })
	public document!: MatchingSchemaDocumentModel;

	/** The operator's reason for the change. Explainability, not decoration. */
	@Column('varchar', { name: 'note', nullable: true, length: 500 })
	public note!: string | null;
}
